import React, { useState, useEffect, useRef, useMemo } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import {
  Modal,
  Typography,
  Box,
  Button as ButtonMui,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  IconButton,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import AlertDialog from "../components/AlertDialog";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { colors } from "../components/Theme/WebTheme";
import moment from "moment/min/moment-with-locales";
import Switch from "@mui/material/Switch";
import { useNavigate,useLocation } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import { FONT_FAMILY } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import TableStyle from '../components/Table/Style';
import localization from '../components/Table/Localization';
import { getLangKey } from "common/src/other/getLangKey";
import { CAR_REJECT_REASONS, carRejectReasonLabel } from "common/src/other/carRejection";
import AddIcon from "@mui/icons-material/Add";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ClearIcon from "@mui/icons-material/Clear";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import {
  C2_FONT,
  c2ModalBoxSx,
  c2PrimaryBtnSx,
  c2SecondaryBtnSx,
} from "../components/conecta2ModalStyles";
import {
  C2,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  C2TableCard,
  C2Select,
  C2StatusChip,
} from "../components/Table/C2UserTable";

/**
 * Detecta si un vehículo fue rechazado previamente y volvieron a subir documentos o actualizarlo.
 */
export function isCarReuploadedAfterReject(car) {
  if (!car) return false;
  if (car.approved) return false;

  // Si tiene marca explícita de reenvío
  if (car.resubmittedAt) {
    if (!car.rejectedAt || Number(car.resubmittedAt) > Number(car.rejectedAt)) {
      return true;
    }
  }

  if (car.docsReuploadedAfterReject) return true;

  // Si fue rechazado previamente y tiene fecha de actualización de documentos o datos posterior al rechazo
  if (car.rejected || car.rejection_code) {
    if (car.updateAt && car.rejectedAt && Number(car.updateAt) > Number(car.rejectedAt)) {
      return true;
    }
    if (car.registrationUpdatedAt && car.rejectedAt && Number(car.registrationUpdatedAt) > Number(car.rejectedAt)) {
      return true;
    }
  }

  return false;
}

export function getCarApprovalStatus(car) {
  if (!car) return "pending";
  if (car.approved) return "approved";
  if (isCarReuploadedAfterReject(car)) return "reuploaded";
  if (car.rejected) return "rejected";
  return "pending";
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  submit3: {
    width: "100%",
    borderRadius: 3,
    marginTop: 2,
    padding: 4,
  },
  paper: {
    width: 500,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
  },
}));

export default function CarsList() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((state) => state.settingsdata.settings);
  const userdata = useSelector((state) => state.usersdata);
  const auth = useSelector((state) => state.auth);
  const { updateUserCar, editCar, approveCar, rejectCar, fetchCarTypes } = api;
  const [driversObj, setDriversObj] = useState();
  const [data, setData] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const carlistdata = useSelector((state) => state.carlistdata);
  const cartypes = useSelector((state) => state.cartypes?.cars);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const classes = useStyles();
  const [role, setRole] = useState(null);
  const {state} = useLocation()
  const [currentPage,setCurrentPage] = useState(null)

  useEffect(() => {
    if (typeof fetchCarTypes === "function") {
      dispatch(fetchCarTypes());
    }
  }, [dispatch, fetchCarTypes]);

  useEffect(()=>{
    setCurrentPage(state?.pageNo)
  },[state])
  
  const HandalePageChange = (page)=>{
    setCurrentPage(page)
  }

  useEffect(() => {
    if (role !== "driver" && userdata.users) {
      let arr = userdata.users.filter(
        (user) =>
          user.usertype === "driver" &&
          ((role === "fleetadmin" &&
            user.fleetadmin &&
            user.fleetadmin === auth.profile.uid) ||
            role === "admin")
      );
      let obj = {}; 
      for (let i = 0; i < arr.length; i++) {
        let user = arr[i];
        obj[user.id] = `${user.firstName} ${user.lastName} (${
          settings.AllowCriticalEditsAdmin ? user.mobile : t("hidden_demo")
        }) ${settings.AllowCriticalEditsAdmin ? user.email : t("hidden_demo")}`;
      }
      setDriversObj(obj);
    }
  }, [
    userdata.users,
    settings.AllowCriticalEditsAdmin,
    role,
    auth.profile.uid,
    t,
  ]);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  useEffect(() => {
    if (!carlistdata?.cars) {
      setData([]);
      return;
    }
    // Conductor: lista propia, sin mapa de drivers
    if (role === "driver") {
      setData(carlistdata.cars);
      return;
    }
    if (driversObj) {
      const updatedData = carlistdata.cars.map((car) => ({
        ...car,
        driverName: driversObj[car?.driver] || " ",
      }));
      setData(updatedData);
    } else {
      setData([]);
    }
  }, [carlistdata?.cars, driversObj, role]);

  const vehicleFilterOptions = useMemo(() => {
    const set = new Set();
    (cartypes || []).forEach((ct) => {
      if (ct?.name) set.add(ct.name);
    });
    (data || []).forEach((d) => {
      if (d.carType) set.add(d.carType);
    });
    const sorted = Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
    return [
      { value: "", label: "Todos los tipos" },
      ...sorted.map((name) => ({
        value: name,
        label: t(getLangKey(name)) || name,
      })),
    ];
  }, [cartypes, data, t]);

  const reuploadedCount = useMemo(() => {
    return (data || []).filter(isCarReuploadedAfterReject).length;
  }, [data]);

  const statusFilterOptions = useMemo(() => {
    return [
      { value: "", label: "Todos los estados" },
      ...(reuploadedCount > 0
        ? [
            {
              value: "reuploaded",
              label: `🔔 Reenviados (${reuploadedCount})`,
            },
          ]
        : []),
      { value: "pending", label: "Pendientes por revisar" },
      { value: "approved", label: "Aprobados" },
      { value: "rejected", label: "Rechazados" },
    ];
  }, [reuploadedCount]);

  const filteredData = useMemo(() => {
    return (data || []).filter((car) => {
      // 1. Filtro por Día (Fecha de creación del vehículo)
      if (dateFilter) {
        if (!car.createdAt) return false;
        const carDay = moment(car.createdAt).format("YYYY-MM-DD");
        if (carDay !== dateFilter) return false;
      }

      // 2. Filtro por Tipo de Vehículo
      if (vehicleFilter && vehicleFilter !== "all") {
        const cType = car.carType || "";
        if (cType.toLowerCase() !== vehicleFilter.toLowerCase()) return false;
      }

      // 3. Filtro por Estado (Pendiente, Aprobado, Rechazado, Reenviado)
      if (statusFilter && statusFilter !== "all") {
        const approval = getCarApprovalStatus(car);
        if (statusFilter === "reuploaded") {
          if (approval !== "reuploaded") return false;
        } else if (statusFilter === "approved") {
          if (approval !== "approved") return false;
        } else if (statusFilter === "rejected") {
          if (approval !== "rejected") return false;
        } else if (statusFilter === "pending") {
          if (approval !== "pending" && approval !== "reuploaded") return false;
        }
      }

      return true;
    });
  }, [data, dateFilter, vehicleFilter, statusFilter]);

  const [selectedImage, setSelectedImage] = useState(null);
  const handleProfileModal = (e) => {
    setProfileModal(false);
    setSelectedImage(null);
  };

  const [userData, setUserData] = useState();
  const [profileModal, setProfileModal] = useState(false);
  const [imageData, setImageData] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectCode, setRejectCode] = useState("reupload_car_image");
  const [rejectOther, setRejectOther] = useState("");
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const [loading, setLoading] = useState(false);

  const openImagePreview = (url, title) => {
    if (!url) return;
    setPreviewImage(url);
    setPreviewTitle(title || t("image"));
    setPreviewModal(true);
  };

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const handleSetProfileModal = (e) => {
    e.preventDefault();
    if(settings.AllowCriticalEditsAdmin){
    if (selectedImage) {
      setLoading(true);
      let finalData = userData;
      finalData.car_image = selectedImage;
      dispatch(editCar(finalData, "UpdateImage"));
      setProfileModal(false);
      setTimeout(() => {
        setSelectedImage(null);
        setLoading(false);
      }, 10000);
    } else {
      setCommonAlert({ open: true, msg: t("choose_image_first") });
    }
    
  }else{
    setCommonAlert({ open: true, msg: t('demo_mode')})
  }
}

  const onClick = (rowData) => {
    setImageData(rowData.car_image);
    setProfileModal(true);
    setUserData(rowData);
  };

  const columns = [
    {
      title: t("createdAt"),
      field: "createdAt",
      editable: "never",
      defaultSort: "desc",
      headerStyle: { whiteSpace: "nowrap", minWidth: 150 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 150 },
      render: (rowData) =>
        rowData.createdAt ? moment(rowData.createdAt).format("lll") : null,
    },
    {
      title: t("driver"),
      field: "driverName",  
      editable: role === "driver" ? "never" : "always",
      headerStyle: { whiteSpace: "nowrap", minWidth: 220 },
      cellStyle: { minWidth: 220, maxWidth: 320 },
      render: (rowData) => rowData.driverName || null,
    },
    {
      title: t("car_type"),
      field: "carType",
      headerStyle: { whiteSpace: "nowrap", minWidth: 130 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 130 },
      render: (rowData) => {
        const isReup = isCarReuploadedAfterReject(rowData);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box component="span" sx={{ fontWeight: 600, fontFamily: C2_FONT }}>
              {rowData.carType ? t(getLangKey(rowData.carType)) : "—"}
            </Box>
            {isReup ? (
              <Tooltip title="⚠️ Este vehículo fue rechazado anteriormente y volvieron a subir documentos para revisión">
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 0.6,
                    py: 0.15,
                    borderRadius: "6px",
                    bgcolor: "#FFF3E0",
                    color: "#E65100",
                    border: "1px solid #FFE0B2",
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    fontFamily: C2_FONT,
                    whiteSpace: "nowrap",
                  }}
                >
                  🔔 Reenviado
                </Box>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    {
      title: t("vehicle_reg_no"),
      field: "vehicleNumber",
      headerStyle: { whiteSpace: "nowrap", minWidth: 120 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 120 },
    },
    {
      title: t("vehicle_model_name"),
      field: "vehicleMake",
      headerStyle: { whiteSpace: "nowrap", minWidth: 130 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 130 },
    },
    {
      title: t("vehicle_model_no"),
      field: "vehicleModel",
      headerStyle: { whiteSpace: "nowrap", minWidth: 120 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 120 },
    },
    {
      title: t("vehicle_color") || "Color",
      field: "vehicleColor",
      headerStyle: { whiteSpace: "nowrap", minWidth: 100 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 100 },
      render: (rowData) => {
        const color = (rowData.vehicleColor || "").toString().trim();
        if (!color) {
          return (
            <Typography
              component="span"
              sx={{ color: "#766293", fontSize: 12, fontFamily: "'Outfit', 'Poppins', sans-serif" }}
            >
              —
            </Typography>
          );
        }
        return (
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 1,
              py: 0.4,
              borderRadius: "8px",
              backgroundColor: "#EBE7FB",
              color: "#1A0F3B",
              fontSize: 12,
              fontFamily: "'Outfit', 'Poppins', sans-serif",
              fontWeight: 600,
            }}
          >
            {color}
          </Box>
        );
      },
    },
    {
      title: t("view_registration") || "Matrícula",
      field: "registration_image",
      editable: "never",
      sorting: false,
      headerStyle: { whiteSpace: "nowrap", minWidth: 110 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 110 },
      render: (rowData) =>
        rowData.registration_image ? (
          <button
            type="button"
            onClick={() =>
              openImagePreview(
                rowData.registration_image,
                t("registration_card") || "Tarjeta de propiedad"
              )
            }
            style={{
              border: "1.5px solid #D7CFEB",
              borderRadius: 10,
              padding: 2,
              background: "#FBFAFE",
              cursor: "pointer",
            }}
            title={t("registration_card") || "Ver tarjeta de propiedad"}
          >
            <img
              alt="Matricula"
              src={rowData.registration_image}
              style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 8, display: "block" }}
            />
          </button>
        ) : (
          <Typography
            component="span"
            sx={{ color: "#766293", fontSize: 12, fontFamily: "'Outfit', 'Poppins', sans-serif" }}
          >
            —
          </Typography>
        ),
    },
    {
      title: t("other_info"),
      field: "other_info",
      headerStyle: { whiteSpace: "nowrap", minWidth: 140 },
      cellStyle: { minWidth: 140 },
      render: (rowData) => {
        const info = (rowData.other_info || "").toString().trim();
        if (!info) {
          return (
            <Typography
              component="span"
              sx={{ color: "#766293", fontSize: 12, fontFamily: "'Outfit', 'Poppins', sans-serif" }}
            >
              —
            </Typography>
          );
        }
        return (
          <Tooltip title={info} arrow>
            <Box
              component="span"
              sx={{
                display: "inline-block",
                maxWidth: 160,
                px: 1,
                py: 0.4,
                borderRadius: "8px",
                backgroundColor: "#EBE7FB",
                color: "#1A0F3B",
                fontSize: 12,
                fontFamily: "'Outfit', 'Poppins', sans-serif",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              }}
            >
              {info}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      title: t("image"),
      field: "car_image",
      initialEditValue:
        "https://cdn.pixabay.com/photo/2012/04/13/20/37/car-33556__480.png",
      headerStyle: { whiteSpace: "nowrap", minWidth: 100 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 100 },
      render: (rowData) =>
        rowData.car_image ? (
          <button
            type="button"
            onClick={() => {
              onClick(rowData);
            }}
            style={{
              border: "1.5px solid #D7CFEB",
              borderRadius: 10,
              padding: 2,
              background: "#FBFAFE",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={t("upload_car_image")}
          >
            <img
              alt="CarImage"
              src={rowData.car_image}
              style={{
                width: 56,
                height: 40,
                objectFit: "cover",
                borderRadius: 8,
                display: "block",
              }}
            />
          </button>
        ) : null,
    },
    {
      title: t("active_status"),
      field: "active",
      editable:  "never" ,
      type: "boolean",
      initialEditValue: true,
      headerStyle: { whiteSpace: "nowrap", minWidth: 90 },
      cellStyle: { whiteSpace: "nowrap", minWidth: 90 },
      render: (rowData) => (
        <Switch
        disabled
          checked={rowData.active}
          onChange={() => handelActiveStatus(rowData)}
        />
      ),
    },
    {
      title: t("approved"),
      field: "approved",
      editable: "never",
      sorting: false,
      headerStyle: { whiteSpace: "nowrap", minWidth: 200 },
      cellStyle: { minWidth: 200 },
      render: (rowData) => {
        const canModerate =
          settings.AllowCriticalEditsAdmin && settings.carApproval && role === "admin";
        const isReup = isCarReuploadedAfterReject(rowData);
        let statusLabel = t("pending") || "Pendiente";
        let tone = "warning";
        if (rowData.approved) {
          statusLabel = t("approved") || "Aprobado";
          tone = "success";
        } else if (isReup) {
          statusLabel = "Reenviado";
          tone = "warning";
        } else if (rowData.rejected) {
          statusLabel = t("car_rejected") || "Rechazado";
          tone = "danger";
        }
        const reasonText = rowData.rejected && !isReup
          ? carRejectReasonLabel(
              rowData.rejection_code,
              i18n.language,
              rowData.rejection_other || rowData.rejection_reason
            )
          : "";
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, minWidth: 160 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <C2StatusChip
                tone={tone}
                label={statusLabel}
              />
              {isReup ? (
                <Tooltip title="⚠️ Volvió a subir documentos tras rechazo">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 0.5,
                      py: 0.15,
                      borderRadius: "6px",
                      bgcolor: "#FFF3E0",
                      color: "#E65100",
                      border: "1px solid #FFE0B2",
                      fontSize: "0.65rem",
                      fontWeight: 800,
                    }}
                  >
                    🔔
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
            {reasonText ? (
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#766293",
                  fontFamily: C2_FONT,
                  lineHeight: 1.3,
                  maxWidth: 180,
                }}
              >
                {reasonText}
              </Typography>
            ) : null}
            {canModerate ? (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {!rowData.approved ? (
                  <ButtonMui
                    size="small"
                    startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                    onClick={() => handleApprove(rowData)}
                    sx={{
                      ...c2PrimaryBtnSx,
                      py: 0.25,
                      px: 1,
                      fontSize: "0.72rem",
                      minWidth: 0,
                    }}
                  >
                    {t("car_approve") || "Aprobar"}
                  </ButtonMui>
                ) : null}
                <ButtonMui
                  size="small"
                  startIcon={<HighlightOffIcon sx={{ fontSize: 16 }} />}
                  onClick={() => openRejectModal(rowData)}
                  sx={{
                    ...c2SecondaryBtnSx,
                    py: 0.25,
                    px: 1,
                    fontSize: "0.72rem",
                    minWidth: 0,
                    color: "#DF2E38",
                    borderColor: "rgba(223,46,56,0.45)",
                  }}
                >
                  {t("car_reject") || "Rechazar"}
                </ButtonMui>
              </Box>
            ) : null}
          </Box>
        );
      },
    },
  ];
  const [selectedRow, setSelectedRow] = useState(null);

  const openRejectModal = (rowData) => {
    setRejectTarget(rowData);
    setRejectCode("reupload_car_image");
    setRejectOther("");
    setRejectModal(true);
  };

  const handleApprove = (rowData) => {
    if (!(settings.AllowCriticalEditsAdmin && settings.carApproval && role === "admin")) {
      setCommonAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    dispatch(approveCar(rowData));
  };

  const handleConfirmReject = () => {
    if (!(settings.AllowCriticalEditsAdmin && settings.carApproval && role === "admin")) {
      setCommonAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    if (!rejectTarget) return;
    if (rejectCode === "other" && !String(rejectOther || "").trim()) {
      setCommonAlert({
        open: true,
        msg: t("car_reject_other_placeholder") || "Describe el motivo…",
      });
      return;
    }
    const reason = carRejectReasonLabel(rejectCode, i18n.language, rejectOther);
    dispatch(
      rejectCar(rejectTarget, {
        code: rejectCode,
        reason,
        otherText: rejectOther,
      })
    );
    setRejectModal(false);
    setRejectTarget(null);
  };
  const handelActiveStatus = (rowData) => {
    const updateData = { ...rowData, active: !rowData.active };
    return updateData;
  };
  const carsTableOptions = {
    ...c2TableOptions(selectedRow),
    pageSize: 20,
    pageSizeOptions: [10, 20, 50, 100],
    headerStyle: {
      ...c2TableOptions(selectedRow).headerStyle,
      whiteSpace: "nowrap",
    },
  };

  const registerCarButton = (
    <ButtonMui
      variant="contained"
      startIcon={<AddIcon />}
      onClick={() => navigate(`/cars/editcar`, { state: { pageNo: currentPage } })}
      sx={{
        textTransform: "none",
        fontFamily: C2_FONT,
        fontWeight: 700,
        borderRadius: "12px",
        px: 2,
        py: 1,
        flexShrink: 0,
        background: "linear-gradient(90deg, #6411ED 0%, #7628FF 100%)",
        boxShadow: "0 6px 16px rgba(100,17,237,0.28)",
        "&:hover": {
          background: "linear-gradient(90deg, #5B0FD9 0%, #6D1FF0 100%)",
        },
      }}
    >
      + Registrar vehículo
    </ButtonMui>
  );

  const emptyStateMessage = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, sm: 6 },
        px: 2,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 88,
          height: 88,
          borderRadius: "50%",
          backgroundColor: "#EBE7FB",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <DirectionsCarOutlinedIcon sx={{ fontSize: 44, color: "#6411ED" }} />
        <Box
          sx={{
            position: "absolute",
            right: 2,
            bottom: 2,
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #6411ED, #7628FF)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(100,17,237,0.35)",
          }}
        >
          <AddIcon sx={{ fontSize: 18, color: "#FBFAFE" }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontFamily: C2_FONT,
          fontWeight: 700,
          fontSize: "1.05rem",
          color: "#1A0F3B",
          textAlign: "center",
        }}
      >
        No hay registros para mostrar
      </Typography>
      <Typography
        sx={{
          fontFamily: C2_FONT,
          fontSize: "0.9rem",
          color: "#766293",
          textAlign: "center",
          maxWidth: 380,
          lineHeight: 1.45,
        }}
      >
        {dateFilter || (vehicleFilter && vehicleFilter !== "all") || (statusFilter && statusFilter !== "all")
          ? "No se encontraron vehículos con los filtros seleccionados."
          : "Aún no tienes vehículos registrados. ¡Registra tu primer vehículo y comienza a operar con Conecta2!"}
      </Typography>
    </Box>
  );

  return carlistdata.loading ? (
    <CircularLoading />
  ) : (
    <div ref={rootRef}>
      <ThemeProvider theme={c2TableTheme}>
        <C2TableCard>
          <MaterialTable
            title=""
            columns={columns}
            onChangePage={(page) => HandalePageChange(page)}
            style={c2TableStyle(isRTL)}
            data={filteredData || []}
            onRowClick={(evt, selectedRow) =>
              setSelectedRow(selectedRow.tableData.id)
            }
            components={{
              Toolbar: (toolbarProps) => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    width: "100%",
                    px: 1,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.25,
                      width: "100%",
                    }}
                  >
                    {/* Controles de filtro a la izquierda */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        flex: "1 1 auto",
                      }}
                    >
                      {/* Burbuja interactiva de vehículos que reenviaron documento tras rechazo */}
                      {reuploadedCount > 0 && (
                        <Box
                          onClick={() =>
                            setStatusFilter((prev) =>
                              prev === "reuploaded" ? "" : "reuploaded"
                            )
                          }
                          role="button"
                          tabIndex={0}
                          title="Filtrar vehículos reenviados tras rechazo"
                          sx={{
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.5,
                            py: 0.65,
                            borderRadius: "999px",
                            backgroundColor:
                              statusFilter === "reuploaded" ? "#5B0FD9" : "#FFF4E5",
                            color:
                              statusFilter === "reuploaded" ? "#FFFFFF" : "#B76E00",
                            border:
                              statusFilter === "reuploaded"
                                ? "1px solid #5B0FD9"
                                : "1px solid #FFE2B8",
                            boxShadow: "0 2px 8px rgba(183,110,0,0.18)",
                            fontFamily: C2_FONT,
                            fontSize: "0.78rem",
                            fontWeight: 800,
                            letterSpacing: "0.2px",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              boxShadow: "0 4px 12px rgba(100,17,237,0.22)",
                            },
                          }}
                        >
                          <NotificationsActiveOutlinedIcon sx={{ fontSize: 16 }} />
                          <span>
                            {reuploadedCount}{" "}
                            {reuploadedCount === 1
                              ? "doc reenviado"
                              : "docs reenviados"}
                          </span>
                          {statusFilter === "reuploaded" && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusFilter("");
                              }}
                              sx={{ p: 0.2, color: "#FFFFFF", ml: 0.25 }}
                            >
                              <ClearIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Box>
                      )}

                      {/* Filtro por estado */}
                      <C2Select
                        size="small"
                        id="cars-status-filter"
                        label="Estado"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusFilterOptions}
                        sx={{ minWidth: 180 }}
                      />

                      {/* Filtro por tipo de vehículo */}
                      <C2Select
                        size="small"
                        id="cars-vehicle-filter"
                        label="Tipo de vehículo"
                        value={vehicleFilter}
                        onChange={(e) => setVehicleFilter(e.target.value)}
                        options={vehicleFilterOptions}
                        sx={{ minWidth: 175 }}
                      />

                      {/* Filtro por día */}
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #D7CFEB",
                          borderRadius: "10px",
                          px: 1,
                          py: 0.45,
                          transition: "border-color 0.2s ease",
                          "&:hover": { borderColor: "#6411ED" },
                        }}
                      >
                        <Typography
                          component="label"
                          htmlFor="cars-date-filter"
                          sx={{
                            fontFamily: C2_FONT,
                            fontSize: "0.74rem",
                            fontWeight: 700,
                            color: "#766293",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Día:
                        </Typography>
                        <input
                          id="cars-date-filter"
                          type="date"
                          value={dateFilter}
                          onChange={(e) => setDateFilter(e.target.value)}
                          style={{
                            border: "none",
                            outline: "none",
                            fontFamily: C2_FONT,
                            fontSize: "0.82rem",
                            color: "#1A0F3B",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                          }}
                        />
                        {dateFilter && (
                          <IconButton
                            size="small"
                            onClick={() => setDateFilter("")}
                            title="Limpiar fecha"
                            sx={{
                              p: 0.25,
                              color: "#766293",
                              "&:hover": { color: "#6411ED" },
                            }}
                          >
                            <ClearIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {/* Botón registrar vehículo a la derecha */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {registerCarButton}
                    </Box>
                  </Box>

                  {/* Barra de búsqueda y exportación */}
                  <Box
                    sx={{
                      width: "100%",
                      "& .MuiToolbar-root": {
                        minHeight: 48,
                        paddingLeft: "0 !important",
                        paddingRight: "0 !important",
                      },
                    }}
                  >
                    <MTableToolbar {...toolbarProps} />
                  </Box>
                </Box>
              ),
            }}
            options={{
              initialPage: state?.pageNo,
              exportButton: true,
              showTitle: false,
              rowStyle: (rowData) => ({
                backgroundColor:
                  selectedRow === rowData.tableData.id
                    ? colors.ROW_SELECTED
                    : colors.WHITE,
              }),
              ...carsTableOptions,
            }}
            localization={{
              ...localization(t),
              body: {
                ...(localization(t).body || {}),
                emptyDataSourceMessage: emptyStateMessage,
              },
            }}
            editable={{
              onRowDelete: (oldData) =>
                settings.AllowCriticalEditsAdmin
                  ? new Promise((resolve, reject) => {
                      setTimeout(() => {
                        if (oldData.active) {
                          reject();
                          alert(t("active_car_delete"));
                        } else {
                          resolve();
                          dispatch(editCar(oldData, "Delete"));
                        }
                      }, 600);
                    })
                  : new Promise((resolve) => {
                      setTimeout(() => {
                        resolve();
                        alert(t("demo_mode"));
                      }, 600);
                    }),
            }}
            actions={[
              {
                icon: "add",
                tooltip: t("add_car"),
                isFreeAction: true,
                hidden: true,
                onClick: (event) =>
                  navigate(`/cars/editcar`, { state: { pageNo: currentPage } }),
              },
              (rowData) => ({
                tooltip: t("edit"),
                icon: () => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      marginRight: 0,
                    }}
                  >
                    <EditIcon />
                  </div>
                ),
                onClick: (event, rowData) => {
                  navigate(`/cars/editcar/${rowData.id}`, {
                    state: { pageNo: currentPage },
                  });
                },
              }),
            ]}
          />
        </C2TableCard>
      </ThemeProvider>
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={profileModal}
        onClose={handleProfileModal}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Box
          sx={{
            ...c2ModalBoxSx,
            maxWidth: { xs: "92%", sm: 560, md: 600 },
            width: "100%",
            direction: isRTL === "rtl" ? "rtl" : "ltr",
          }}
        >
          {/* Header con título, datos de vehículo y botón cerrar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: C2_FONT,
                  fontWeight: 800,
                  fontSize: "1.2rem",
                  color: "#1A0F3B",
                  lineHeight: 1.25,
                }}
              >
                {t("upload_car_image")}
              </Typography>
              {userData && (userData.vehicleNumber || userData.vehicleMake) && (
                <Typography
                  sx={{
                    fontFamily: C2_FONT,
                    fontSize: "0.85rem",
                    color: "#766293",
                    mt: 0.25,
                    fontWeight: 600,
                  }}
                >
                  {[
                    userData.vehicleMake,
                    userData.vehicleModel,
                    userData.vehicleNumber ? `Placa: ${userData.vehicleNumber}` : "",
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={handleProfileModal}
              size="small"
              sx={{ color: "#766293", "&:hover": { color: "#1A0F3B" } }}
              title={t("cancel")}
            >
              <ClearIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Selector de archivo estilizado */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1.25,
              border: "1.5px dashed #D7CFEB",
              borderRadius: "12px",
              backgroundColor: "#F9F7FE",
              mb: 2,
            }}
          >
            <input
              type="file"
              accept="image/*"
              style={{
                fontFamily: C2_FONT,
                fontSize: "0.85rem",
                color: "#221448",
                width: "100%",
                cursor: "pointer",
              }}
              name={t("image")}
              onChange={(event) => {
                if (event.target.files && event.target.files[0]) {
                  setSelectedImage(event.target.files[0]);
                }
              }}
            />
          </Box>

          {/* Contenedor de la imagen del vehículo: grande, centrado y responsivo */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              borderRadius: "16px",
              border: "1.5px solid #D7CFEB",
              backgroundColor: "#F4F0FD",
              boxShadow: "0 8px 24px rgba(100,17,237,0.08)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              minHeight: 260,
              maxHeight: 400,
              p: 1.5,
              boxSizing: "border-box",
            }}
          >
            {selectedImage ? (
              <img
                alt="Vehículo"
                src={URL.createObjectURL(selectedImage)}
                style={{
                  maxWidth: "100%",
                  maxHeight: 370,
                  objectFit: "contain",
                  borderRadius: 12,
                  display: "block",
                  margin: "0 auto",
                }}
              />
            ) : imageData ? (
              <img
                alt="Vehículo"
                src={imageData}
                style={{
                  maxWidth: "100%",
                  maxHeight: 370,
                  objectFit: "contain",
                  borderRadius: 12,
                  display: "block",
                  margin: "0 auto",
                }}
              />
            ) : (
              <Typography
                sx={{
                  fontFamily: C2_FONT,
                  color: "#766293",
                  fontSize: "0.9rem",
                }}
              >
                No hay imagen disponible
              </Typography>
            )}

            {selectedImage && !loading && (
              <Tooltip title={t("cancel")}>
                <IconButton
                  onClick={() => setSelectedImage(null)}
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    backgroundColor: "rgba(229, 57, 53, 0.92)",
                    color: "#FFFFFF",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    "&:hover": { backgroundColor: "#D32F2F" },
                  }}
                  size="small"
                >
                  <CancelIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Botones de acción */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress sx={{ color: "#6411ED" }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.25,
                mt: 2.5,
              }}
            >
              <ButtonMui
                onClick={handleProfileModal}
                variant="outlined"
                sx={c2SecondaryBtnSx}
              >
                {t("cancel")}
              </ButtonMui>
              <ButtonMui
                onClick={handleSetProfileModal}
                variant="contained"
                sx={c2PrimaryBtnSx}
              >
                {t("save")}
              </ButtonMui>
            </Box>
          )}
        </Box>
      </Modal>
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={previewModal}
        onClose={() => {
          setPreviewModal(false);
          setPreviewImage(null);
        }}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Box
          sx={{
            ...c2ModalBoxSx,
            maxWidth: 720,
            width: "92%",
            direction: isRTL === "rtl" ? "rtl" : "ltr",
          }}
        >
          <Typography
            sx={{
              fontFamily: C2_FONT,
              fontWeight: 800,
              fontSize: "1.15rem",
              color: "#1A0F3B",
              mb: 2,
            }}
          >
            {previewTitle}
          </Typography>
          {previewImage ? (
            <Box
              sx={{
                borderRadius: "14px",
                border: "1.5px solid #D7CFEB",
                overflow: "hidden",
                backgroundColor: "#F4F2FE",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 280,
                mb: 2,
              }}
            >
              <img
                alt={previewTitle}
                src={previewImage}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonMui
              onClick={() => {
                setPreviewModal(false);
                setPreviewImage(null);
              }}
              variant="contained"
              sx={c2PrimaryBtnSx}
            >
              {t("ok") || "OK"}
            </ButtonMui>
          </Box>
        </Box>
      </Modal>
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={rejectModal}
        onClose={() => {
          setRejectModal(false);
          setRejectTarget(null);
        }}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Box sx={{ ...c2ModalBoxSx, maxWidth: 480, width: "92%", direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: "1.15rem", color: "#1A0F3B", mb: 1 }}>
            {t("car_reject_title") || "Rechazar vehículo"}
          </Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.88rem", color: "#766293", mb: 2 }}>
            {t("car_reject_pick_reason") || "Elige el motivo del rechazo"}
            {rejectTarget?.vehicleNumber ? ` · ${rejectTarget.vehicleNumber}` : ""}
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={rejectCode}
              onChange={(e) => setRejectCode(e.target.value)}
            >
              {CAR_REJECT_REASONS.map((r) => (
                <FormControlLabel
                  key={r.code}
                  value={r.code}
                  control={<Radio sx={{ color: "#6411ED", "&.Mui-checked": { color: "#6411ED" } }} />}
                  label={
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.92rem", color: "#1A0F3B" }}>
                      {i18n.language?.toLowerCase().startsWith("en") ? r.en : r.es}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
          {rejectCode === "other" ? (
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={rejectOther}
              onChange={(e) => setRejectOther(e.target.value)}
              placeholder={t("car_reject_other_placeholder") || "Describe el motivo…"}
              sx={{ mt: 1.5, fontFamily: C2_FONT }}
            />
          ) : null}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2.5, flexWrap: "wrap" }}>
            <ButtonMui
              variant="outlined"
              onClick={() => {
                setRejectModal(false);
                setRejectTarget(null);
              }}
              sx={c2SecondaryBtnSx}
            >
              {t("cancel")}
            </ButtonMui>
            <ButtonMui variant="contained" onClick={handleConfirmReject} sx={c2PrimaryBtnSx}>
              {t("car_reject_confirm") || "Confirmar rechazo"}
            </ButtonMui>
          </Box>
        </Box>
      </Modal>
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </div>
  );
}
